import React, { FC, memo, ReactElement, ReactNode, RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  ArrowLeftRight,
  Banknote,
  ChevronDown,
  ChevronRight,
  CircleAlert,
  CircleCheck,
  CircleX,
  ClipboardCheck,
  Clock,
  Coins,
  Ellipsis,
  FileCheck,
  FileSpreadsheet,
  FileText,
  FileUp,
  History,
  Smartphone,
  SquarePen,
  Timer,
  Trash2,
  Upload,
  Video,
} from 'lucide-react';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import i18next from 'i18next';
import { DateTime } from 'luxon';
import { PatientListDto } from '../../../../shared/dtos/patient.dto';
import { formatDateWithRelative, formatTime } from '../../util/date-utils';
import { formatDuration } from '../../util/duration-utils';
import { createPortal } from 'react-dom';
import { PatientTherapyStatus, TherapyBillStatus } from '../../../../shared/utils/enums';
import Image from 'react-bootstrap/Image';
import { ChangeTherapist } from './PatientsTable';
import TrainingProgressBar, { TrainingProgressBadge } from './TrainingProgressBar';

type PatientRowProps = {
  row: PatientListDto;
  showAdditionalInfo: boolean;
  isOpen: boolean;
  onToggle: () => void;
  onNavigate?: () => void;

  onEditPatient?: (row: PatientListDto) => void;
  onUploadApplication?: (row: PatientListDto) => void;
  onUploadFinal?: (row: PatientListDto) => void;
  onSetCompletionConversation?: (patientId: string) => void;
  onCompletePatient?: (patientId: string) => void;
  onExportFile?: (patientId: string) => void;
  onBillPatient?: (row: PatientListDto) => void;
  onDeletePatient?: (patientId: string) => void;
  onCancelPatient?: (patientId: string) => void;
  onTherapistChange?: (data: ChangeTherapist) => void;
}

type MenuAction =
  | 'edit'
  | 'uploadApplication'
  | 'uploadFinal'
  | 'export'
  | 'bill'
  | 'therapistChange'
  | 'completionConversation'
  | 'complete'
  | 'delete'
  | 'cancel';

type PatientMenuItem = {
  action: MenuAction;
  icon: ReactNode;
  label: string;
  disabledReason?: string;
  isDanger?: boolean;
}

type PatientMenuGroup = {
  label?: string;
  items: PatientMenuItem[];
}

type MenuPosition = {
  top: number;
  left: number;
  placement: 'bottom' | 'top';
}

const MENU_WIDTH: number = 200;
const VIEWPORT_PADDING: number = 8;
const MENU_Z_INDEX: number = 1065;

const PATIENT_STATUS = {
  [PatientTherapyStatus.PLANNED]: {
    style: { backgroundColor: '#fff9e5', color: '#cc8b00' },
    icon: <Clock size={18} />,
    label: 'patientsTable.status.planned',
  },
  [PatientTherapyStatus.STARTED]: {
    style: { backgroundColor: '#e9eefc', color: '#2655a6' },
    icon: <Timer size={18} />,
    label: 'patientsTable.status.started',
  },
  [PatientTherapyStatus.COMPLETED]: {
    style: { backgroundColor: '#ebfaf0', color: '#37954d' },
    icon: <CircleCheck size={18} />,
    label: 'patientsTable.status.completed'
  },
  [PatientTherapyStatus.CANCELLED]: {
    style: { backgroundColor: '#f4f4f5', color: '#646369' },
    icon: <CircleAlert size={18} />,
    label: 'patientsTable.status.cancelled',
  }
};

const TREATMENT_ENDED_HINT: { [status in PatientTherapyStatus]?: string } = {
  [PatientTherapyStatus.COMPLETED]: 'patientsTable.menu.completedHint',
  [PatientTherapyStatus.CANCELLED]: 'patientsTable.menu.cancelledHint',
};

const THERAPY_BILLING_STATUS = {
  [TherapyBillStatus.OPEN]: {
    style: { backgroundColor: '#fde7e7', color: '#ac202b' },
    icon: <CircleAlert size={18} />,
    label: 'patientsTable.therapyBillingStatus.open',
  },
  [TherapyBillStatus.PLANNED]: {
    style: { backgroundColor: '#fff9e5', color: '#cc8b00' },
    icon: <Clock size={18} />,
    label: 'patientsTable.therapyBillingStatus.planned',
  },
  [TherapyBillStatus.PAYED]: {
    style: { backgroundColor: '#ebfaf0', color: '#37954d' },
    icon: <Coins size={18} />,
    label: 'patientsTable.therapyBillingStatus.payed',
  },
};

type StatBlockProps = {
  label: string;
  icon?: ReactNode;
  trailing?: ReactNode;
  children: ReactNode;
};

function StatBlock({ label, icon, trailing, children }: StatBlockProps): ReactElement {
  return (
    <div className="d-flex flex-column gap-2 bg-white border rounded-3 p-3 h-100">
      <span className="d-flex align-items-center justify-content-between gap-2">
        <span className="small text-body-secondary text-uppercase" style={{ letterSpacing: '0.04em' }}>{label}</span>
        {trailing}
      </span>
      <div className="d-flex align-items-center gap-2">
        {icon}
        <div className="flex-grow-1">{children}</div>
      </div>
    </div>
  );
}

function CountDot({ color, count, tooltip, outlined }: { color: string; count: number; tooltip: string; outlined?: boolean }): ReactElement {
  return (
    <OverlayTrigger placement="top" overlay={<Tooltip id={`vc-${tooltip}`}>{tooltip}</Tooltip>}>
      <span className="d-inline-flex align-items-center gap-1">
        <span
          className="d-inline-block rounded-circle"
          style={{
            width: '0.7rem',
            height: '0.7rem',
            boxSizing: 'border-box',
            backgroundColor: outlined ? 'transparent' : color,
            border: `2px solid ${color}`,
          }}
        />
        <span className="fw-semibold">{count}</span>
      </span>
    </OverlayTrigger>
  );
}

function buildStatusTooltipLines(row: PatientListDto): string[] {
  const lines: string[] = [];

  if (row.patientInfo.status === PatientTherapyStatus.CANCELLED) {
    if (row.cancelledAt) {
      lines.push(i18next.t('patientsTable.status.cancelledOn', {
        date: DateTime.fromISO(row.cancelledAt).toLocaleString(DateTime.DATE_SHORT),
      }));
    }

    if (row.cancellationReason) {
      lines.push(row.cancellationReason);
    }
  }

  if (row.patientInfo.status === PatientTherapyStatus.COMPLETED && row.completedAt) {
    lines.push(i18next.t('patientsTable.status.completedOn', {
      date: DateTime.fromISO(row.completedAt).toLocaleString(DateTime.DATE_SHORT),
    }));
  }

  return lines;
}

type MenuItemProps = {
  item: PatientMenuItem;
  onSelect: (event: React.MouseEvent) => void;
};

function MenuItem({ item, onSelect }: MenuItemProps): ReactElement {
  const className: string = `dropdown-item d-flex align-items-center gap-2 small${item.isDanger ? ' text-danger' : ''}`;

  if (!item.disabledReason) {
    return (
      <button type="button" className={className} onClick={onSelect}>
        {item.icon}
        <span>{item.label}</span>
      </button>
    );
  }

  return (
    <OverlayTrigger
      placement="left"
      overlay={<Tooltip id={`menu-${item.action}`}>{item.disabledReason}</Tooltip>}
    >
      <span className="d-block">
        <button type="button" className={`${className} disabled`} disabled style={{ pointerEvents: 'none' }}>
          {item.icon}
          <span>{item.label}</span>
        </button>
      </span>
    </OverlayTrigger>
  );
}

const PatientRow: FC<PatientRowProps> = ({
  row,
  showAdditionalInfo,
  isOpen,
  onToggle,
  onNavigate,
  onEditPatient,
  onUploadApplication,
  onUploadFinal,
  onSetCompletionConversation,
  onCompletePatient,
  onBillPatient,
  onExportFile,
  onDeletePatient,
  onCancelPatient,
  onTherapistChange,
}: PatientRowProps): ReactElement => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null);
  const [menuMaxHeight, setMenuMaxHeight] = useState<number | undefined>();

  const menuRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement | null>(null);

  const handleMenuToggle = (event: React.MouseEvent): void => {
    event.stopPropagation();

    const rect: DOMRect = event.currentTarget.getBoundingClientRect();

    setIsMenuOpen((prev: boolean) => {
      if (prev) {
        setMenuPosition(null);
        setMenuMaxHeight(undefined);
        return false;
      }

      setMenuPosition({
        top: rect.bottom,
        left: rect.right - MENU_WIDTH,
        placement: 'bottom'
      });

      return true;
    });
  };

  useLayoutEffect(() => {
    if (!isMenuOpen || !menuRef.current || !menuPosition) {
      return;
    }

    const menuElement: HTMLDivElement = menuRef.current;
    const menuRect: DOMRect = menuElement.getBoundingClientRect();

    const viewportWidth: number = window.innerWidth;
    const viewportHeight: number = window.innerHeight;

    const currentTop: number = menuPosition.top;
    const currentLeft: number = menuPosition.left;

    const spaceBelow: number = viewportHeight - currentTop - VIEWPORT_PADDING;
    const spaceAbove: number = currentTop - VIEWPORT_PADDING;

    let nextPlacement : 'bottom' | 'top' = 'bottom';
    let nextTop: number = currentTop;

    if (menuRect.height > spaceBelow && spaceAbove > spaceBelow) {
      nextPlacement = 'top';
      nextTop = currentTop - menuRect.height;
    }

    const minTop: number = VIEWPORT_PADDING;
    const maxTop: number = viewportHeight - menuRect.height - VIEWPORT_PADDING;
    nextTop = Math.max(minTop, Math.min(nextTop, maxTop));

    const minLeft: number = VIEWPORT_PADDING;
    const maxLeft: number = viewportWidth - menuRect.width - VIEWPORT_PADDING;
    const nextLeft: number = Math.max(minLeft, Math.min(currentLeft, maxLeft));

    const availableHeight: number = nextPlacement === 'bottom' ? viewportHeight - nextTop - VIEWPORT_PADDING : nextTop - VIEWPORT_PADDING;

    const nextMaxHeight: number | undefined = menuRect.height > availableHeight ? Math.max(120, availableHeight) : undefined;

    if (nextTop !== menuPosition.top || nextLeft !== menuPosition.left || nextPlacement !== menuPosition.placement) {
      setMenuPosition({ top: nextTop, left: nextLeft, placement: nextPlacement });
    }

    if (nextMaxHeight !== menuMaxHeight) {
      setMenuMaxHeight(nextMaxHeight);
    }

  }, [isMenuOpen, menuPosition]);

  useEffect(() => {
    if (!isMenuOpen) {
      return;
    }

    const close = (): void => setIsMenuOpen(false);

    const handleClickOutside = (event: MouseEvent): void => {
      if (!menuRef.current) {
        return;
      }

      if (event.target instanceof Node && !menuRef.current.contains(event.target)) {
        close();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);

    return (): void => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [isMenuOpen]);


  const handleMenuAction = (event: React.MouseEvent, action: MenuAction): void => {
    event.stopPropagation();
    setIsMenuOpen(false);

    switch (action) {
      case 'edit':
        onEditPatient?.(row);
        break;
      case 'uploadApplication':
        onUploadApplication?.(row);
        break;
      case 'uploadFinal':
        onUploadFinal?.(row);
        break;
      case 'export':
        onExportFile?.(row.id);
        break;
      case 'bill':
        onBillPatient?.(row);
        break;
      case 'therapistChange':
        onTherapistChange?.({ patientId: row.id, currentTherapistId: row.therapistInfo?.id ?? '' });
        break;
      case 'completionConversation':
        onSetCompletionConversation?.(row.id);
        break;
      case 'complete':
        onCompletePatient?.(row.id);
        break;
      case 'cancel':
        onCancelPatient?.(row.id);
        break;
      case 'delete':
        onDeletePatient?.(row.id);
        break;
    }
  };

  const buildMenuGroups = (): PatientMenuGroup[] => {
    const treatmentEndedHintKey: string | undefined = TREATMENT_ENDED_HINT[row.patientInfo.status];
    const treatmentEndedHint: string | undefined = treatmentEndedHintKey
      ? i18next.t(treatmentEndedHintKey)
      : undefined;

    const administration: PatientMenuItem[] = [];
    const documents: PatientMenuItem[] = [];
    const treatmentEnd: PatientMenuItem[] = [];
    const removal: PatientMenuItem[] = [];

    if (onEditPatient) {
      administration.push({
        action: 'edit',
        icon: <SquarePen size={18} className="me-2" />,
        label: i18next.t('patientsTable.menu.edit'),
      });
    }

    if (onTherapistChange) {
      administration.push({
        action: 'therapistChange',
        icon: <ArrowLeftRight size={18} className="me-2" />,
        label: i18next.t('patientsTable.menu.changeTherapist'),
      });
    }

    if (onBillPatient) {
      administration.push({
        action: 'bill',
        icon: <Banknote size={18} className="me-2" />,
        label: i18next.t(row.therapyBillingInfo ? 'patientsTable.menu.billEdit' : 'patientsTable.menu.billCreate'),
      });
    }

    if (onUploadApplication) {
      documents.push({
        action: 'uploadApplication',
        icon: <Upload size={18} className="me-2" />,
        label: i18next.t('patientsTable.menu.uploadApplication'),
      });
    }

    if (onUploadFinal) {
      documents.push({
        action: 'uploadFinal',
        icon: <FileUp size={18} className="me-2" />,
        label: i18next.t('patientsTable.menu.uploadFinal'),
      });
    }

    if (onExportFile) {
      documents.push({
        action: 'export',
        icon: <FileSpreadsheet size={18} className="me-2" />,
        label: i18next.t('patientsTable.menu.export'),
      });
    }

    if (onSetCompletionConversation) {
      treatmentEnd.push({
        action: 'completionConversation',
        icon: <ClipboardCheck size={18} className="me-2" />,
        label: i18next.t('patientsTable.menu.completionConversation'),
        disabledReason: row.completionDocumentUrl
          ? undefined
          : i18next.t('patientsTable.menu.completionConversationHint'),
      });
    }

    if (onCompletePatient) {
      treatmentEnd.push({
        action: 'complete',
        icon: <CircleCheck size={18} className="me-2" />,
        label: i18next.t('patientsTable.menu.complete'),
        disabledReason: treatmentEndedHint,
      });
    }

    if (onCancelPatient) {
      treatmentEnd.push({
        action: 'cancel',
        icon: <CircleX size={18} className="me-2" />,
        label: i18next.t('patientsTable.menu.cancel'),
        disabledReason: treatmentEndedHint,
      });
    }

    if (onDeletePatient) {
      removal.push({
        action: 'delete',
        icon: <Trash2 size={18} className="me-2" />,
        label: i18next.t('patientsTable.menu.delete'),
        isDanger: true,
      });
    }

    const groups: PatientMenuGroup[] = [
      { label: i18next.t('patientsTable.menu.groups.administration'), items: administration },
      { label: i18next.t('patientsTable.menu.groups.documents'), items: documents },
      { label: i18next.t('patientsTable.menu.groups.treatmentEnd'), items: treatmentEnd },
      { items: removal },
    ];

    return groups.filter((group: PatientMenuGroup) => group.items.length > 0);
  };

  return (
    <>
      <tr onClick={onNavigate} className="align-middle" style={{ cursor: onNavigate ? 'pointer' : 'default', height: 64 }}>
        <td style={{ width: 32 }}>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onToggle();
            }}
            onMouseDown={(event) => event.stopPropagation()}
            className="btn btn-link p-0 text-muted"
          >
            {isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} /> }
          </button>
        </td>
        <td>
          <div className="fw-semibold text-truncate" title={row.patientInfo.fullName}>{row.patientInfo.fullName}</div>
          <div className="text-muted small text-truncate" title={row.patientInfo.email}>{row.patientInfo.email}</div>
        </td>
        <td>{row.patientInfo.ssn ? `${row.patientInfo.ssn.slice(0, 4)} ${row.patientInfo.ssn.slice(4)}` : '-'}</td>
        <td>{
          row.patientInfo.onBoardedAt ?
            DateTime.fromISO(row.patientInfo.onBoardedAt).toLocaleString(DateTime.DATE_SHORT)
            : '-'}
        </td>
        <td>
          {row.nextAppointment ? (
            <>
              <div className="text-truncate" title={formatDateWithRelative(row.nextAppointment)}>
                {formatDateWithRelative(row.nextAppointment)}
              </div>
              <div className="text-muted small text-truncate">{formatTime(row.nextAppointment)}</div>
            </>
          ) : '-'}
        </td>
        <td>
          {((): ReactElement => {
            const config = PATIENT_STATUS[row.patientInfo.status];

            const badge: ReactElement = (
              <span style={{
                ...config.style,
                padding: '2px 8px',
                borderRadius: '10px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                fontWeight: 600,
              }}>
                {config.icon}
                <span>{i18next.t(config.label).toUpperCase()}</span>
              </span>
            );

            const tooltipLines: string[] = buildStatusTooltipLines(row);

            if (tooltipLines.length === 0) {
              return badge;
            }

            return (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id={`status-${row.id}`}>
                    {tooltipLines.map((line: string) => <div key={line}>{line}</div>)}
                  </Tooltip>
                }
              >
                {badge}
              </OverlayTrigger>
            );
          })()}
        </td>
        <td>{row.trainingPlanEndDate ? DateTime.fromISO(row.trainingPlanEndDate, { zone: 'utc' }).toLocaleString(DateTime.DATE_SHORT) : '-'}</td>
        <td>
          {((): ReactElement => {
            const programName: string = (i18next.language === 'de'
              ? row.telerehabilitationProgram?.germanName
              : row.telerehabilitationProgram?.englishName) ?? '-';
            return <div className="text-truncate" title={programName}>{programName}</div>;
          })()}
        </td>
        <td>
          <div className="d-flex flex-column gap-1">
            <div className="d-flex align-items-center gap-2">
              <FileText size={16} className="text-muted" />
              <span className="text-muted small">{i18next.t('patientsTable.documentApplicationShort')}:</span>
              {row.applicationDocumentUrl ? (
                <a
                  className="small text-decoration-none"
                  href={row.applicationDocumentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(event) => event.stopPropagation()}
                  onMouseDown={(event) => event.stopPropagation()}
                >
                  {i18next.t('patientsTable.applicationDocument')}
                </a>
              ) : (
                <span>-</span>
              )}
            </div>
            <div className="d-flex align-items-center gap-2">
              <FileCheck size={16} className="text-muted" />
              <span className="text-muted small">{i18next.t('patientsTable.documentCompletionShort')}:</span>
              {row.completionDocumentUrl ? (
                <a
                  className="small text-decoration-none"
                  href={row.completionDocumentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(event) => event.stopPropagation()}
                  onMouseDown={(event) => event.stopPropagation()}
                >
                  {i18next.t('patientsTable.completionDocument')}
                </a>
              ) : (
                <span>-</span>
              )}
            </div>
          </div>
        </td>
        {showAdditionalInfo && (
          <>
            <td>
              {((): ReactElement => {
                const billingInfo = row.therapyBillingInfo;

                if (!billingInfo) {
                  return (<span>-</span>);
                }

                const config = THERAPY_BILLING_STATUS[billingInfo.status];

                const isPayed: boolean = billingInfo.status === TherapyBillStatus.PAYED;
                const content: string = isPayed && billingInfo.billedAt
                  ? DateTime.fromISO(billingInfo.billedAt).toLocaleString(DateTime.DATE_SHORT)
                  : i18next.t(config.label).toUpperCase();

                return (
                  <span style={{
                    ...config.style,
                    padding: '2px 8px',
                    borderRadius: '10px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontWeight: 600,
                  }}>
                    {config.icon}
                    <span>{content}</span>
                  </span>
                );
              })()}
            </td>
            <td>{row.therapyBillingInfo ? i18next.t('patientsTable.billedUnit', { count: row.therapyBillingInfo.unitAmount }) : '-'}</td>
            <td>
              <div className="d-flex align-items-center gap-2">
                <Image
                  src={row.therapistInfo?.profilePicture ?? '../default_profile.png'}
                  roundedCircle
                  className='bg-light'
                  style={{ width: '28px', height: '28px', objectFit: 'cover' }}
                />
                <span>{row.therapistInfo?.fullName ?? '-'}</span>
              </div>
            </td>
          </>
        )}

        <td className="position-sticky end-0 bg-white z-2 text-end" style={{ width: 32 }}>
          <button
            type="button"
            className="btn btn-link p-0 text-muted"
            onClick={handleMenuToggle}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <Ellipsis size={18} />
          </button>

          {isMenuOpen && menuPosition &&
            createPortal(
              <div
                ref={menuRef}
                className="dropdown-menu show shadow-sm py-1 position-fixed"
                style={{
                  minWidth: `${MENU_WIDTH}px`,
                  top: menuPosition.top,
                  left: menuPosition.left,
                  zIndex: MENU_Z_INDEX,
                  maxHeight: menuMaxHeight,
                  overflowY: menuMaxHeight ? 'auto' : undefined,
                }}
              >
                {buildMenuGroups().map((group: PatientMenuGroup, groupIndex: number) => (
                  <div key={group.label ?? 'removal'}>
                    {groupIndex > 0 && <div className="dropdown-divider" />}
                    {group.label && <h6 className="dropdown-header">{group.label}</h6>}
                    {group.items.map((item: PatientMenuItem) => (
                      <MenuItem
                        key={item.action}
                        item={item}
                        onSelect={(event: React.MouseEvent) => handleMenuAction(event, item.action)}
                      />
                    ))}
                  </div>
                ))}
              </div>,
              document.body
            )}
        </td>

      </tr>

      {isOpen && (
        <tr>
          <td style={{ width: 32 }} className="bg-body-tertiary"></td>

          <td className="py-4 bg-body-tertiary" colSpan={showAdditionalInfo ? 11 : 8}>
            <div
              className="border-start border-3 border-primary ps-4 pe-3"
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(300px, 1.7fr) repeat(auto-fit, minmax(165px, 1fr))',
                gap: '0.875rem',
                alignItems: 'stretch',
              }}
            >
              <StatBlock
                label={i18next.t('patientsTable.trainingProgress')}
                trailing={<TrainingProgressBadge totalPercentage={row.trainingInfo.totalPercentage} />}
              >
                <TrainingProgressBar
                  totalMinutes={row.trainingInfo.totalMinutes}
                  totalTargetMinutes={row.trainingInfo.totalTargetMinutes}
                  totalPercentage={row.trainingInfo.totalPercentage}
                  normalMinutes={row.trainingInfo.normalMinutes}
                  enduranceMinutes={row.trainingInfo.enduranceMinutes}
                  countedEnduranceMinutes={row.trainingInfo.countedEnduranceMinutes}
                  enduranceLimitMinutes={row.trainingInfo.enduranceLimitMinutes}
                  maximumEnduranceTrainingPercentage={row.trainingInfo.maximumEnduranceTrainingPercentage}
                  endurancePercentage={row.trainingInfo.endurancePercentage}
                />
              </StatBlock>

              <StatBlock
                label={i18next.t('patientsTable.videoCalls')}
                icon={<Video size={16} className="text-muted" />}
              >
                <span className="d-inline-flex align-items-center gap-3">
                  <span style={{ fontSize: '1.125rem', fontWeight: 500 }}>{formatDuration(row.videoCallInfo.totalDuration)}</span>
                  <span className="d-inline-flex align-items-center gap-3">
                    <CountDot
                      color="var(--bs-success)"
                      count={row.videoCallInfo.currentCount}
                      tooltip={i18next.t('patientsTable.videoCallsDone')}
                    />
                    <CountDot
                      color="#6c757d"
                      count={row.videoCallInfo.plannedCount}
                      tooltip={i18next.t('patientsTable.videoCallsPlanned')}
                      outlined
                    />
                    <CountDot
                      color="var(--bs-warning)"
                      count={row.videoCallInfo.billableCount}
                      tooltip={i18next.t('patientsTable.videoCallsBillable')}
                    />
                  </span>
                </span>
              </StatBlock>

              <StatBlock
                label={i18next.t('patientsTable.lastTraining')}
                icon={<History size={16} className="text-muted" />}
              >
                <span>{formatDateWithRelative(row.trainingInfo.lastDate)}</span>
              </StatBlock>

              <StatBlock
                label={i18next.t('patientsTable.lastAppStart')}
                icon={<Smartphone size={16} className="text-muted" />}
              >
                <span>{formatDateWithRelative(row.patientInfo.lastAppStart)}</span>
              </StatBlock>
            </div>
          </td>

          <td style={{ width: 32 }} className="bg-body-tertiary"></td>
        </tr>
      )}
    </>
  );
};

export default memo(PatientRow);